/** @jsx jsx */
import React, { useState } from 'react';
import { css, jsx } from '@compiled/react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment, useQueryLoader } from 'react-relay';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import StandardButton from '@atlaskit/button/standard-button';
import IssuesIcon from '@atlaskit/icon/core/work-items';
import { Box, Stack, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { fg } from '@atlassian/jira-feature-gating';
import { JiraSiteAri } from '@atlassian/ari/jira/site';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger/src/index.tsx';
import { ModalEntryPointContainer } from '@atlassian/jira-entry-point-modal-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { designModalEntryPoint } from '@atlassian/jira-issue-design-modal/entrypoint.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	fireUIAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import type { designCard_versionIssueAssociatedDesigns_devOpsDesign$key } from '@atlassian/jira-relay/src/__generated__/designCard_versionIssueAssociatedDesigns_devOpsDesign.graphql';
import type { designCard_versionIssueAssociatedDesigns_jiraVersion$key } from '@atlassian/jira-relay/src/__generated__/designCard_versionIssueAssociatedDesigns_jiraVersion.graphql';
import designIssuesModalQuery, {
	type issuesModal_designIssueQuery,
} from '@atlassian/jira-relay/src/__generated__/issuesModal_designIssueQuery.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { CARD_WIDTH } from '../../../common/constants.tsx';
import { SimpleIssueCard } from '../../../common/ui/simple-issue-card/index.tsx';
import { IssuesModal, IssueModalLoading } from './issues-modal/index.tsx';
import messages from './messages.tsx';
import { formatRelativeDate } from './utils.tsx';

type Props = {
	jiraVersion: designCard_versionIssueAssociatedDesigns_jiraVersion$key;
	devOpsDesign: designCard_versionIssueAssociatedDesigns_devOpsDesign$key;
};

const DATE_FORMAT = {
	year: 'numeric',
	month: 'long',
	day: 'numeric',
	hour: 'numeric',
	minute: 'numeric',
} as const;

const runtimeProps = {};
export const DesignCard = ({ devOpsDesign, jiraVersion }: Props): JSX.Element => {
	const { formatDate, formatMessage } = useIntl();
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [designIssuesModalQueryRef, loadDesignIssuesModalQuery] =
		useQueryLoader<issuesModal_designIssueQuery>(designIssuesModalQuery);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const cloudId = useCloudId();
	const siteAri = JiraSiteAri.create({ siteId: cloudId }).toString();

	const data = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment designCard_versionIssueAssociatedDesigns_devOpsDesign on DevOpsDesign {
				id
				url @required(action: THROW)
				displayName @required(action: THROW)
				lastUpdated @required(action: THROW)
				provider(id: $cloudAri) @required(action: THROW) {
					logoUrl @required(action: THROW)
				}
				associatedIssues(first: 2) {
					edges {
						node {
							... on JiraIssue {
								id
								...simpleIssueCard_versionIssueAssociatedDesigns
							}
							__typename
						}
					}
				}
			}
		`,
		devOpsDesign,
	);

	const version = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment designCard_versionIssueAssociatedDesigns_jiraVersion on JiraVersion {
				id @required(action: THROW)
			}
		`,
		jiraVersion,
	);

	const onLoadDesignIssuesModalQuery = () => {
		if (designIssuesModalQueryRef == null) {
			loadDesignIssuesModalQuery({
				cloudAri: siteAri,
				designAris: [data.id],
				versionId: version.id,
			});
		}
	};

	const associatedIssuesNodes = (data.associatedIssues?.edges ?? [])
		.filter(Boolean)
		.map((edge) => edge.node)
		.filter((node) => node?.__typename === 'JiraIssue')
		.filter(Boolean);
	const issues =
		associatedIssuesNodes.length === 1 && associatedIssuesNodes[0].__typename === 'JiraIssue' ? (
			<SimpleIssueCard jiraIssue={associatedIssuesNodes[0]} openIssueInSidebar />
		) : (
			<>
				<span
					css={manyIssuesButtonStyles}
					onMouseEnter={onLoadDesignIssuesModalQuery}
					onFocus={onLoadDesignIssuesModalQuery}
				>
					<StandardButton
						spacing="compact"
						appearance="subtle"
						onClick={(_, analyticsEvent) => {
							onLoadDesignIssuesModalQuery();
							setIsModalOpen(true);
							fireUIAnalytics(analyticsEvent, 'designLinkedIssuesSeeAllModal');
						}}
						iconBefore={
							<IssuesIcon
								color={token('color.icon.accent.blue')}
								label={formatMessage(
									fg('jira-issue-terminology-update-m4')
										? messages.issueIconLabelIssueTerminologyRefreshM4
										: messages.issueIconLabel,
								)}
							/>
						}
					>
						{formatMessage(
							fg('jira-issue-terminology-update-m4')
								? messages.manyIssuesIssueTerminologyRefreshM4
								: messages.manyIssues,
						)}
					</StandardButton>
				</span>
				{designIssuesModalQueryRef != null && isModalOpen && (
					<Placeholder name="version-detaiols-design-issues-modal" fallback={<IssueModalLoading />}>
						<IssuesModal
							queryRef={designIssuesModalQueryRef}
							onClose={() => setIsModalOpen(false)}
						/>
					</Placeholder>
				)}
			</>
		);

	/**
	 * Implementation wise DesignModal component is issue-centric. Hence we need an issue that is linked to this design to be able to
	 * open the DesignModal preview. It doens't matter which issue we pick as all of them are linked to the current deisng, hence is the
	 * term `representativeIssueAri`.
	 */
	const representativeIssueAri =
		associatedIssuesNodes.length > 0 && associatedIssuesNodes[0].__typename === 'JiraIssue'
			? associatedIssuesNodes[0].id
			: '';

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(designModalEntryPoint, {
		siteAri,
		issueAri: representativeIssueAri,
		designAri: data.id,
		cloudId,
	});
	const modalTriggerRef = useEntryPointButtonTrigger(entryPointActions);

	return (
		<ContextualAnalyticsData
			sourceName="versionAssociatedDesignCard"
			sourceType={SCREEN}
			attributes={{
				hasMultipleIssues: associatedIssuesNodes.length > 1,
			}}
		>
			<Box xcss={cardContainerStyles}>
				<img src={data.provider.logoUrl} css={iconStyles} alt="" />
				<Stack xcss={titleStackStyles}>
					{/* eslint-disable-next-line @atlaskit/design-system/no-html-anchor */}
					<a
						css={titleStyles}
						href={data.url}
						target="_blank"
						onClick={() =>
							fireUIAnalytics(createAnalyticsEvent({}), 'link clicked', 'designTitleLink')
						}
					>
						{data.displayName}
					</a>
					<Tooltip content={formatDate(data.lastUpdated, DATE_FORMAT)} position="top">
						{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
						<p css={lastUpdatedStyles}>{formatRelativeDate(new Date(data.lastUpdated))}</p>
					</Tooltip>
					<div css={openPreviewStyles}>
						<StandardButton
							ref={modalTriggerRef}
							shouldFitContainer
							spacing="compact"
							onClick={(_, analyticsEvent) =>
								fireUIAnalytics(analyticsEvent, 'openDesignPreviewButton')
							}
						>
							{formatMessage(messages.openPreview)}
						</StandardButton>
					</div>
					{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
					<p css={linkedToStyles}>{formatMessage(messages.linkedTo)}</p>
					{issues}
				</Stack>
				<ModalEntryPointContainer
					entryPointReferenceSubject={entryPointReferenceSubject}
					id="jiraDesignModalInVersionDetailsPage"
					packageName="design-modal"
					entryPointActions={entryPointActions}
					runtimeProps={runtimeProps}
				/>
			</Box>
			<FireScreenAnalytics />
		</ContextualAnalyticsData>
	);
};

const cardContainerStyles = xcss({
	display: 'flex',
	border: '3px',
	paddingTop: 'space.100',
	paddingBottom: 'space.100',
	paddingLeft: 'space.150',
	paddingRight: 'space.150',
	boxShadow: 'elevation.shadow.raised',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minWidth: `${CARD_WIDTH}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxWidth: `${CARD_WIDTH}px`,
});

const iconStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	height: '24px',
	minWidth: '24px',
	marginTop: token('space.075'),
});

const titleStackStyles = xcss({
	flex: 1,
	marginLeft: 'space.100',
	maxWidth: '312px', // CARD_WIDTH - paddings - icon width
});
const lastUpdatedStyles = css({
	margin: 0,
	font: token('font.body.small'),
	fontWeight: token('font.weight.regular'),
	color: token('color.text.subtlest'),
});
const openPreviewStyles = css({
	width: '120px',
	marginTop: token('space.150'),
	marginBottom: token('space.150'),
});
const linkedToStyles = css({
	margin: 0,
	font: token('font.body.small'),
	fontWeight: token('font.weight.regular'),
	color: token('color.text.subtlest'),
});

const titleStyles = css({
	textDecoration: 'none',
	color: 'inherit',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':hover': {
		textDecoration: 'underline',
		color: 'inherit',
	},
	fontWeight: token('font.weight.semibold'),
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

const manyIssuesButtonStyles = css({
	marginTop: token('space.100'),
	paddingBottom: token('space.050'),
	marginLeft: token('space.negative.150'),
});
